import { inject, Injectable } from '@angular/core';
import { AppointmentServiceDao } from '../db-models/appointment-service-dao';
import { AppointmentsSlotsDao } from '../db-models/free-appointment-dao';
import { CartItem, CartItemReferenceService } from '../models/cart.model';
import { AppointmentState } from '../models/state.model';
import { AppointmentService } from './appointment.service';
import { LocalStorageService } from './local-storage.service';
import { UtilService } from './util.service';
import { TaxableLine, vatTypeLabelKey } from './vat-calculation.util';
import { WidgetUtilService } from './widget-util.service';

@Injectable({
  providedIn: 'root',
})
export class AppointmentCartService {

  private appointmentService = inject(AppointmentService);
  private localStorageService = inject(LocalStorageService);
  private utilService = inject(UtilService);
  private widgetUtilService = inject(WidgetUtilService);

  totalCartDuration = 0;
  totalCartPrice = 0;
  addonsCustomFieldsPrice = 0;
  /** Taxable lines collected from every cart item — net price + rate, no coupon applied. */
  totalCartTaxableLines: TaxableLine[] = [];

  addToCart(
    appointment: AppointmentsSlotsDao,
    appointmentState: AppointmentState,
    appointmentServicesAvailable: AppointmentServiceDao[],
    cartLsName: string,
    cart: CartItem[],
    overwrite: boolean,
    ignoreRelatedServiceId: boolean,
    services: number[],
    relatedAppointmentCardId: number,
    callback: any
  ): void {
    const cartItem = this.createCartItem(
      appointment,
      appointmentServicesAvailable,
      ignoreRelatedServiceId,
      appointmentState.store,
      appointmentState.date,
      services,
      relatedAppointmentCardId
    );
    this.localStorageService.getItem(cartLsName, (lsCart: any) => {
      cart = this.initCartValue(lsCart, cart);

      if (overwrite) {
        cart.push(cartItem);
      } else {
        cart = [];
        cart.push(cartItem);
      }

      this.utilService.updateLocalStorage(cartLsName, cart);
      this.recalculateCart(cart);
      callback(cart);
    });
  }

  removeFromCart(
    cartItemId: string,
    cartLsName: string,
    cart: CartItem[],
    callback: any
  ): void {
    this.localStorageService.getItem(cartLsName, (lsCart: any) => {
      cart = this.initCartValue(lsCart, cart);
      const deleteResult = this.deleteItemFromCart(cart, cartItemId);
      cart = deleteResult.cart;
      const deletedItem = deleteResult.deletedItem;
      this.utilService.updateLocalStorage(cartLsName, cart);
      this.recalculateCart(cart);
      callback(cart, deletedItem);
    });
  }

  recalculateCart(cart: CartItem[]): void {
    if (!cart || cart.length === 0) return;

    // Reset totals
    this.totalCartDuration = 0;
    this.totalCartPrice = 0;
    this.totalCartTaxableLines = [];

    // `plus` adds VAT from these lines, `inclusive` shows the VAT they already contain; `exclude`
    // has no VAT at all. Callers gate the on-top maths on isVatAddedOnTop themselves.
    const collectTaxableLines = vatTypeLabelKey(this.widgetUtilService.widgetConf?.widget_vat_type) !== 'exclude';

    for (const item of cart) {
      // Add up the duration
      this.totalCartDuration += item.total.duration;

      const price = item.total.price;

      // totalCartPrice stays NET — VAT is computed and added on top by the caller (after the
      // coupon), never folded in here.
      if (price > 0) {
        this.totalCartPrice += price;
      } else if (price === -1 && this.totalCartPrice === 0) {
        this.totalCartPrice = -1;
      }

      if (collectTaxableLines) {
        this.totalCartTaxableLines.push(...this.resolveItemTaxableLines(item));
      }
    }
    this.totalCartPrice += this.addonsCustomFieldsPrice;
  }

  /**
   * A cart restored from localStorage (persisted before `taxableLines` existed on `CartItemTotal`)
   * has no `taxableLines` at all — falling straight through to VAT 0 would silently send a NET
   * PaymentIntent for a plus/exclude partner. `total.services` (the raw matched service DAOs,
   * price + tax.rate) has always been persisted, so rebuild the lines from it instead of trusting
   * the missing field. Only when even that is absent do we give up — loudly, not silently.
   */
  private resolveItemTaxableLines(item: CartItem): TaxableLine[] {
    if (item.total.taxableLines?.length) {
      return item.total.taxableLines;
    }

    const services = item.total.services as ({ price?: number; tax?: { rate?: number } })[] | undefined;
    if (services?.length) {
      const rebuilt: TaxableLine[] = [];
      for (const service of services) {
        const netPrice = Number(service?.price);
        const rate = service?.tax ? Number(service.tax.rate) : 0;
        if (netPrice > 0 && rate > 0) {
          rebuilt.push({ netCents: Math.round(netPrice * 100), rateHundredths: Math.round(rate * 100) });
        }
      }
      if (rebuilt.length || !(Number(item.total.price) > 0 && Number(item.total.tax) > 0)) {
        return rebuilt;
      }
    }

    // item.total.tax > 0 says this item IS taxable, but neither taxableLines nor a rebuildable
    // services array survived — VAT for this line cannot be computed. Surfacing it loudly (rather
    // than silently contributing 0) so a legacy-net booking on a plus/exclude partner is at least
    // diagnosable instead of indistinguishable from "correctly untaxed".
    console.error(
      '[vat] cart item has a taxable rate but no recoverable taxable lines — VAT for this item will be 0',
      { cartItemId: item.cartItemId, total: item.total }
    );
    return [];
  }


  initCartValue(lsCart: CartItem[] | undefined | null, cart: CartItem[]): CartItem[] {
    cart = lsCart ?? [];
    return cart;
  }

  deleteItemFromCart(cart: CartItem[], cartItemId: string): { cart: CartItem[], deletedItem: CartItem | null } {
    let deletedItem: CartItem | null = null;

    // Delete from cart if not empty
    for (let i = cart.length - 1; i >= 0; i--) {
      if (cart[i].cartItemId === cartItemId) {
        deletedItem = cart[i]; // Capture the item before deleting
        cart.splice(i, 1);
        break; // Exit after finding and deleting the first match
      }
    }

    return { cart, deletedItem };
  }

  createCartItem(
    appointment: AppointmentsSlotsDao,
    appointmentServicesAvailable: AppointmentServiceDao[],
    ignoreRelatedServiceId: boolean,
    store: number,
    date: Date,
    services: number[],
    relatedAppointmentCardId: number
  ): CartItem {
    const cartItem: CartItem = {
      appointmentDaoObject: appointment,
      storeId: store,
      storeName: appointment?.store?.name,
      store: appointment?.store,
      date: date,
      location: appointment?.location,
      startTime: appointment.start,
      shortStart: Number(appointment.short_start),
      endTime: appointment.end,
      workerId: appointment?.worker?.id,
      servicesIds: services,
      cartItemId: this.appointmentService.createCartItemId(appointment, date),
      workerName: appointment?.worker?.resource_name,
      workerEmail: appointment?.worker?.email,
      total: this.appointmentService.getServicesByIds(
        { serviceIds: services, returnKey: 'total' },
        appointmentServicesAvailable
      ),
      relatedServiceIds: [],
      relatedServices: [],
      relatedAppointmentCardId,
      referenceServices: [],
      parentServiceId: appointment.parentServiceId,
      workerUuids: appointment.workerUuids,
      combiServiceWorkersMapping: appointment.combiServiceWorkersMapping
    };

    const referenceServices = Array<CartItemReferenceService>();
    if (cartItem?.servicesIds?.length) {
      const foundedService = appointmentServicesAvailable.find(item => cartItem?.servicesIds?.indexOf(item?.id) > -1);
      if (foundedService) {
        referenceServices.push({
          serviceId: foundedService.id,
          serviceName: foundedService.name,
          serviceCategoryId: foundedService.appointment_service_category_id
        });
      }
    }

    cartItem.referenceServices = referenceServices;

    if (!ignoreRelatedServiceId) {
      for (const temp of appointmentServicesAvailable) {
        if (
          temp?.related_appointment_service_id &&
          services?.indexOf(temp.id) > -1
        ) {
          cartItem.relatedServiceIds.push(temp?.related_appointment_service_id);
        }
      }

      for (const temp of cartItem?.relatedServiceIds) {
        for (const temp1 of appointmentServicesAvailable) {
          if (temp1.id === temp) {
            cartItem.relatedServices.push(temp1);
          }
        }
      }
    }
    return cartItem;
  }

  resetCart(cartLsName: string): void {
    this.utilService.updateLocalStorage(cartLsName, this.initCartValue([], []));
    this.totalCartDuration = 0;
    this.totalCartPrice = 0;
    this.totalCartTaxableLines = [];
  }
}
